/*
  Customer review carousel - RTL fix.

  The store is forced to direction:rtl by the Elixir/SenseApps RTL app. In an RTL
  flex row the slides lay out right-to-left, so slide N sits to the LEFT of slide 0
  and reaching it means translating the track to the RIGHT (+). The carousel code
  inlined in sections/shop-product-details.liquid always translates left (-), which
  on load pushes the whole track ~283px off-screen and leaves an empty gap with the
  pagination dots underneath.

  This file takes the carousel over: it renames the element id so the theme's inline
  initialiser bails out on its `if (!carousel) return;`, replaces the node to drop any
  listeners already attached, and re-implements the behaviour direction-aware and in
  pixels (the original stepped in % of the track's own width and ignored the 12px gap).
*/
(function () {
  'use strict';

  var ID_PREFIX = 'customer-review-carousel-';

  function init(carousel) {
    var track = carousel.querySelector('.customer-review-slides');
    var slides = [].slice.call(carousel.querySelectorAll('.customer-review-slide'));
    var dots = [].slice.call(carousel.querySelectorAll('.pagination-dot'));
    if (!track || !slides.length) return;

    var isRTL = getComputedStyle(carousel).direction === 'rtl';
    var dirSign = isRTL ? 1 : -1;

    var currentSlide = Math.floor(slides.length / 2);
    var startX = 0;
    var startY = 0;
    var currentX = 0;
    var isDragging = false;
    var axisLocked = false;
    var startOffset = 0;

    // One step is the slide width plus the flex gap, measured in px.
    function step() {
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return slides[0].getBoundingClientRect().width + gap;
    }

    function offsetFor(index) {
      return dirSign * index * step();
    }

    function setTransform(px, instant) {
      if (instant) track.style.transition = 'none';
      track.style.transform = 'translateX(' + px + 'px)';
      if (instant) {
        void track.offsetHeight; // force reflow before restoring the transition
        track.style.transition = 'transform 0.4s ease-out';
      }
    }

    function goToSlide(index, instant) {
      if (index >= slides.length) currentSlide = 0;
      else if (index < 0) currentSlide = slides.length - 1;
      else currentSlide = index;

      setTransform(offsetFor(currentSlide), instant);

      slides.forEach(function (slide, i) {
        slide.classList.toggle('active', i === currentSlide);
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('active', i === currentSlide);
      });
    }

    function pointer(e) {
      return e.type.indexOf('mouse') !== -1 ? e : e.touches[0];
    }

    function handleStart(e) {
      var p = pointer(e);
      if (!p) return;
      isDragging = true;
      axisLocked = false;
      startX = p.pageX;
      startY = p.pageY;
      currentX = startX;
      // Same formula as goToSlide - the original used -5% here against -3% there,
      // so the card jumped as soon as it was touched.
      startOffset = offsetFor(currentSlide);
      track.style.transition = 'none';
    }

    function handleMove(e) {
      if (!isDragging) return;
      var p = pointer(e);
      if (!p) return;
      var diffX = p.pageX - startX;
      var diffY = p.pageY - startY;

      // Only claim the gesture once it is clearly horizontal, otherwise the page
      // can no longer be scrolled vertically from inside the carousel.
      if (!axisLocked) {
        if (Math.abs(diffX) < 8 && Math.abs(diffY) < 8) return;
        if (Math.abs(diffY) > Math.abs(diffX)) {
          isDragging = false;
          return;
        }
        axisLocked = true;
      }
      if (e.cancelable) e.preventDefault();

      currentX = p.pageX;
      setTransform(startOffset + diffX);
    }

    function handleEnd() {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = 'transform 0.4s ease-out';

      var diff = currentX - startX;
      if (Math.abs(diff) > 50) {
        // Mirrored in RTL: dragging right brings the next slide into view.
        var forward = isRTL ? diff > 0 : diff < 0;
        goToSlide(currentSlide + (forward ? 1 : -1));
      } else {
        goToSlide(currentSlide);
      }
    }

    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        goToSlide(index);
      });
    });

    track.addEventListener('mousedown', handleStart);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);

    track.addEventListener('touchstart', handleStart, { passive: true });
    track.addEventListener('touchmove', handleMove, { passive: false });
    track.addEventListener('touchend', handleEnd);
    track.addEventListener('touchcancel', handleEnd);

    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    // Offsets are in px, so they have to be recomputed whenever the width changes -
    // including the case where init ran while the carousel still had zero width.
    var lastWidth = 0;
    function reflow() {
      var w = slides[0].getBoundingClientRect().width;
      if (!w || w === lastWidth) return;
      lastWidth = w;
      goToSlide(currentSlide, true);
    }
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(reflow).observe(track);
    }
    window.addEventListener('resize', reflow);

    goToSlide(currentSlide, true);
  }

  function takeOver(root) {
    var scope = root || document;
    var found = false;
    [].slice
      .call(scope.querySelectorAll('[id^="' + ID_PREFIX + '"]'))
      .forEach(function (el) {
        if (!el.querySelector('.customer-review-slides')) return;
        if (el.dataset.crcRtlFixed) return; // already taken over
        found = true;
        // Rename first so the theme's inline initialiser cannot find it and returns.
        el.id = el.id + '--rtl-fixed';
        // Replace the node so any listeners already attached to it are dropped.
        var fresh = el.cloneNode(true);
        fresh.dataset.crcRtlFixed = '1';
        el.parentNode.replaceChild(fresh, el);
        init(fresh);
      });
    return found;
  }

  takeOver();
  document.addEventListener('DOMContentLoaded', function () {
    takeOver();
  });
  document.addEventListener('shopify:section:load', function (e) {
    takeOver(e.target);
  });
})();
